import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    FormControlLabel,
    Typography,
    Checkbox,
    useMediaQuery,
} from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IRoleCreator } from '../../../../types/global/helper';
import { supportedCurrency } from '../../../../constants/currency';
import { CurrencyTextInput } from '../../../CurrencyTextInput/CurrencyTextInput';
import { RichTextEditor } from '../../../Common/RichTextEditor';

interface RoleManagementDialogProps {
    open: boolean;
    role?: IRoleCreator;
    onClose: () => void;
    onSave: (role: Partial<IRoleCreator>) => void;
}

export const RoleManagementDialog: React.FC<RoleManagementDialogProps> = ({ open, role, onClose, onSave }) => {
    const { themeStyle } = useThemeContext();
    const isMobile = useMediaQuery('(max-width:768px)');
    const [formData, setFormData] = useState<Partial<IRoleCreator>>({
        character: '',
        description: '',
        gender: 'male',
        min_age: 6,
        max_age: 70,
        compensation: 0,
        currency: 'USD',
        role_type: { label: 'Cast', value: 'cast' },
        expertise: 'beginner',
        required_media: [],
        requires_subscription: false,
        nudity_or_kissing: false,
    });

    // Update form data when role changes
    useEffect(() => {
        if (role) {
            setFormData({
                character: role.character || '',
                description: role.description || '',
                gender: role.gender || '',
                min_age: role.min_age,
                max_age: role.max_age,
                compensation: role.compensation || 0,
                currency: role.currency || 'USD',
                role_type: role.role_type || { label: 'Cast', value: 'cast' },
                required_media: role.required_media || [],
                requires_subscription: role.requires_subscription,
                nudity_or_kissing: role.nudity_or_kissing || false,
                ...role,
            });
        }
        return () => {
            setFormData({});
        };
    }, [role]);

    const handleInputChange = (field: keyof IRoleCreator, value: any) => {
        setFormData((prev: Partial<IRoleCreator>) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        onSave(formData);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    backgroundColor: theme[themeStyle].secondaryBg,
                    color: theme[themeStyle].primary_white,
                    ...(isMobile && {
                        height: '100dvh',
                        maxHeight: '100dvh',
                        margin: 0,
                        borderRadius: 0,
                    }),
                },
            }}>
            <DialogTitle
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: isMobile ? '1.25rem' : '1.5rem',
                    color: theme[themeStyle].primary_white,
                    ...(isMobile && {
                        padding: '16px',
                        borderBottom: `1px solid ${theme[themeStyle].grey}`,
                    }),
                }}>
                {role ? 'Edit Role' : 'Create New Role'}
            </DialogTitle>
            <DialogContent
                sx={{
                    ...(isMobile && {
                        height: 'calc(100dvh - 140px)', // Account for header and actions
                        overflowY: 'auto',
                        padding: '16px',
                    }),
                }}>
                <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Character Name"
                            value={formData.character}
                            onChange={e => handleInputChange('character', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].grey,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].primary_orange,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <RichTextEditor
                            label={'Character Description'}
                            value={formData.description!}
                            onChange={v => handleInputChange('description', v)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Gender</InputLabel>
                            <Select
                                value={formData.gender}
                                onChange={e => handleInputChange('gender', e.target.value)}
                                label="Gender"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].primary_white,
                                            '& .MuiMenuItem-root': {
                                                color: theme[themeStyle].primary_white,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].background_gradient.end,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: theme[themeStyle].primary_orange,
                                                    color: theme[themeStyle].black,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].grey,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].primary_orange,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="any">Any</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Min Age"
                                value={formData.min_age}
                                onChange={e => handleInputChange('min_age', parseInt(e.target.value))}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].grey,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Max Age"
                                value={formData.max_age}
                                onChange={e => handleInputChange('max_age', parseInt(e.target.value))}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].grey,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <CurrencyTextInput
                                value={formData.compensation !== null ? Number(formData.compensation) : 0}
                                minAmount={0}
                                details={0}
                                supportedCurrency={supportedCurrency}
                                initialCurrency={formData.currency}
                                initialValue={Number(formData.compensation)}
                                message={''}
                                onChange={({ currency, value }) => {
                                    handleInputChange('currency', currency);
                                    handleInputChange('compensation', value);
                                }}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Expertise Level</InputLabel>
                            <Select
                                value={formData.expertise}
                                onChange={e => handleInputChange('expertise', e.target.value)}
                                label="Expertise Level"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].primary_white,
                                            '& .MuiMenuItem-root': {
                                                color: theme[themeStyle].primary_white,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].background_gradient.end,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: theme[themeStyle].primary_orange,
                                                    color: theme[themeStyle].black,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].grey,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].primary_orange,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                <MenuItem value="beginner">Beginner</MenuItem>
                                <MenuItem value="mid-level">Mid Level</MenuItem>
                                <MenuItem value="expert">Expert</MenuItem>
                                <MenuItem value="professional">Professional</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Role Type</InputLabel>
                            <Select
                                value={typeof formData.role_type === 'object' ? formData.role_type.value : 'cast'}
                                onChange={e =>
                                    handleInputChange('role_type', { label: e.target.value, value: e.target.value })
                                }
                                label="Role Type"
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].primary_white,
                                            '& .MuiMenuItem-root': {
                                                color: theme[themeStyle].primary_white,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].background_gradient.end,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: theme[themeStyle].primary_orange,
                                                    color: theme[themeStyle].black,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].grey,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].primary_orange,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                <MenuItem value="cast">Cast</MenuItem>
                                <MenuItem value="crew">Crew</MenuItem>
                                <MenuItem value="technical">Technical</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormControlLabel
                                title={'Requires Subscription'}
                                control={
                                    <Checkbox
                                        checked={(formData['requires_subscription'] as unknown as boolean) || false}
                                        onChange={() => {
                                            handleInputChange(
                                                'requires_subscription',
                                                !formData['requires_subscription'],
                                            );
                                        }}
                                        sx={{
                                            color: theme[themeStyle].gray_sidebar,
                                            '&.Mui-checked': {
                                                color: theme[themeStyle].highlight,
                                            },
                                            '&.MuiCheckbox-indeterminate': {
                                                color: theme[themeStyle].highlight,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ color: theme[themeStyle].tag_white }}>
                                        Verified Thspians only
                                    </Typography>
                                } // Label changes based on state
                            />

                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 12,
                                    color: theme[themeStyle].tag_white,
                                    mt: 1,
                                }}>
                                Enabling this option restricts applications for this role to verified Thspians only.
                            </Typography>
                        </FormControl>
                    </Grid>

                    {/* Nudity or Kissing - Only show for cast roles */}
                    {typeof formData.role_type === 'object' && formData.role_type.value === 'cast' && (
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormControlLabel
                                    title={'Nudity or Kissing'}
                                    control={
                                        <Checkbox
                                            checked={(formData['nudity_or_kissing'] as unknown as boolean) || false}
                                            onChange={() => {
                                                handleInputChange('nudity_or_kissing', !formData['nudity_or_kissing']);
                                            }}
                                            sx={{
                                                color: theme[themeStyle].gray_sidebar,
                                                '&.Mui-checked': {
                                                    color: theme[themeStyle].highlight,
                                                },
                                                '&.MuiCheckbox-indeterminate': {
                                                    color: theme[themeStyle].highlight,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: theme[themeStyle].tag_white }}>
                                            This role involves nudity or kissing scenes
                                        </Typography>
                                    }
                                />

                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                        color: theme[themeStyle].tag_white,
                                        mt: 1,
                                    }}>
                                    Check this box if the role involves nudity or kissing scenes. This helps talent make
                                    informed decisions about applying.
                                </Typography>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    ...(isMobile && {
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: theme[themeStyle].secondaryBg,
                        padding: '16px',
                        borderTop: `1px solid ${theme[themeStyle].grey}`,
                        justifyContent: 'space-between',
                    }),
                }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: theme[themeStyle].tag_white,
                        ...(isMobile && {
                            flex: 1,
                            marginRight: 1,
                        }),
                    }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{
                        backgroundColor: theme[themeStyle].primary_orange,
                        ...(isMobile && {
                            flex: 1,
                            marginLeft: 1,
                        }),
                    }}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
